/**
 * What, if anything, to tell somebody about their connection — SRS D5.
 *
 * A dropped connection costs a teacher nothing: their pen keeps working, everything
 * they draw is kept, and it merges when the line comes back. So the rule is to say
 * nothing at all about a brief one. A banner every time a lift goes past a router
 * would teach the class that the board is unreliable, when it is not.
 *
 * Only once an outage has lasted long enough to be worth knowing about does anything
 * appear, and even then it says the work is safe, because it is.
 */

import type { ConnectionStatus } from './sync.js';

/** Nothing at all, "reconnecting", or "disconnected" — docs/06-UI-SPEC.md §17.3. */
export type ConnectionNotice = 'none' | 'reconnecting' | 'disconnected';

/** SRS D5 — an outage shorter than this is never mentioned. */
export const QUIET_MS = 90_000;

/** SRS D5 — past this it has stopped being a blip. */
export const LOST_MS = 10 * 60_000;

/** What the screen should say, given how long the connection has been down. */
export function noticeFor(status: ConnectionStatus, offlineForMs: number): ConnectionNotice {
  if (status === 'connected') return 'none';
  if (offlineForMs < QUIET_MS) return 'none';
  return offlineForMs < LOST_MS ? 'reconnecting' : 'disconnected';
}

/**
 * How long until what the screen should say changes, or null if it never will.
 *
 * Used to wake up exactly on the boundary rather than asking every second, so a
 * three-hour lesson does not spend the whole time checking a clock.
 */
export function msUntilNextNotice(status: ConnectionStatus, offlineForMs: number): number | null {
  if (status === 'connected') return null;
  if (offlineForMs < QUIET_MS) return QUIET_MS - offlineForMs;
  if (offlineForMs < LOST_MS) return LOST_MS - offlineForMs;
  return null;
}
